import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Edit, Trash2, MessageCircle } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'completed', label: 'Completed' },
];

const statusBadge = (status) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800',
    contacted: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
};

const QuotesManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ status: 'pending', admin_notes: '' });

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/quote-requests`, getAuthHeader());
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openEdit = (item) => {
    setCurrent(item);
    setForm({ status: item.status || 'pending', admin_notes: item.admin_notes || '' });
    setShowModal(true);
  };

  const handleQuickStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quote-requests/${id}/status`, { status }, getAuthHeader());
      toast.success(`Marked as ${status}`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote request?')) return;
    try {
      await axios.delete(`${API}/quote-requests/${id}`, getAuthHeader());
      toast.success('Quote request deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.patch(`${API}/quote-requests/${current.id}`, form, getAuthHeader());
      toast.success('Quote updated');
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const whatsappLink = (q) => {
    const phone = (q.phone || '').replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Hi ${q.name}, this is Vision Tattoo following up on your quote request. ` +
      `For your ${q.tattoo_size} ${q.style} tattoo on ${q.placement}, ` +
      `the estimated price range is ${q.estimated_price_range}. Let's discuss next steps!`
    );
    return `https://wa.me/${phone}?text=${msg}`;
  };

  return (
    <div data-testid="quotes-content">
      <h2 className="font-heading text-3xl mb-8 flex items-center gap-3">
        <FileText size={28} /> Quote Requests
      </h2>

      {items.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center text-gray-500">No quote requests yet.</div>
      ) : (
        <div className="space-y-4">
          {items.map((quote) => (
            <div key={quote.id} className="bg-white border border-gray-200 p-6" data-testid={`quote-item-${quote.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                <div>
                  <h3 className="font-heading text-xl font-bold">{quote.name}</h3>
                  <p className="font-mono text-sm text-gray-500">{quote.email} • {quote.phone}</p>
                </div>
                <span className={`font-mono text-xs uppercase px-3 py-1 rounded-full ${statusBadge(quote.status)}`}>
                  {quote.status}
                </span>
              </div>

              <div className="grid md:grid-cols-3 gap-4 text-sm mb-3">
                <div><span className="font-bold">Size:</span> {quote.tattoo_size}</div>
                <div><span className="font-bold">Placement:</span> {quote.placement}</div>
                <div><span className="font-bold">Style:</span> {quote.style}</div>
              </div>

              {quote.preferred_artist && (
                <p className="font-mono text-sm text-gray-700 mb-2" data-testid={`quote-preferred-artist-${quote.id}`}>
                  <span className="font-bold uppercase">Preferred Artist:</span> {quote.preferred_artist}
                </p>
              )}

              {quote.estimated_price_range && (
                <p className="font-mono text-sm text-gray-700 mb-2">
                  <span className="font-bold uppercase">Estimate:</span> {quote.estimated_price_range}
                </p>
              )}

              {quote.description && (
                <p className="text-gray-600 mb-3">{quote.description}</p>
              )}

              {quote.admin_notes && (
                <p className="font-body text-sm text-gray-600 italic mb-3 bg-yellow-50 p-3 border-l-4 border-yellow-300">
                  📝 {quote.admin_notes}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                <select
                  value={quote.status}
                  onChange={(e) => handleQuickStatus(quote.id, e.target.value)}
                  data-testid={`quote-quick-status-${quote.id}`}
                  className="border border-gray-300 px-3 py-1 font-mono text-xs uppercase"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => openEdit(quote)}
                  data-testid={`quote-edit-${quote.id}`}
                  className="flex items-center gap-1 bg-gray-100 px-3 py-1 font-mono text-xs uppercase hover:bg-gray-200"
                >
                  <Edit size={14} /> Notes
                </button>
                {quote.phone && (
                  <a
                    href={whatsappLink(quote)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`quote-whatsapp-${quote.id}`}
                    className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 font-mono text-xs uppercase hover:bg-green-200"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                )}
                <button
                  onClick={() => handleDelete(quote.id)}
                  data-testid={`quote-delete-${quote.id}`}
                  className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 font-mono text-xs uppercase hover:bg-red-200"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Edit Quote Request"
        footer={
          <button
            onClick={handleSubmit}
            data-testid="quote-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800"
          >
            Save Changes
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="quote-form-status"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Internal Notes</label>
            <textarea
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              rows={5}
              data-testid="quote-form-notes"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
              placeholder="e.g. Called on 30/4 — wants smaller size, follow up next week"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuotesManager;
